import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { US_POINTS, CONTACT_INFO } from "../data";
import { applyPageMeta, PAGE_SEO_CONFIG } from "../lib/seoMeta";

export default function GetQuote() {
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    description: "",
    website_url: "", // Honeypot
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'

  useEffect(() => {
    // Set metadata for Få et tilbud (max 512px title, max 970px description)
    applyPageMeta(PAGE_SEO_CONFIG.quote);

    // Fetch services from CMS
    fetch("https://api.dev.auxo.dk/public/content/services")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setServices(data);
          if (data.length > 0) {
            setSelectedService(data[0].id); // Default to first service
          } else {
            setSelectedService("andet");
          }
        } else {
          setSelectedService("andet");
        }
      })
      .catch((err) => {
        console.error("Error fetching services:", err);
        setSelectedService("andet");
      });
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Navn skal udfyldes";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Telefonnummer skal udfyldes";
    } else if (!/^[0-9\s+]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = "Ugyldigt telefonnummer";
    }
    if (!formData.email.trim()) {
      newErrors.email = "E-mail skal udfyldes";
    } else if (!/\S+@\S+\.\S+/.test(formData.email.trim())) {
      newErrors.email = "Ugyldig e-mailadresse";
    }
    if (!formData.description.trim()) {
      newErrors.description = "Beskrivelse skal udfyldes";
    }
    if (!selectedService) {
      newErrors.service = "Vælg venligst en ydelse";
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const allServiceOptions = [
    ...services,
    {
      id: "andet",
      title: "Andet",
      description: "Har du en anden opgave eller et specielt projekt? Vælg denne og beskriv det nedenfor.",
      icon: "fa-ellipsis",
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check honeypot
    if (formData.website_url) {
      setSubmitStatus("success");
      return;
    }

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    const selectedServiceObj = allServiceOptions.find((s) => s.id === selectedService);
    const serviceTitle = selectedServiceObj ? selectedServiceObj.title : "Andet";

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          description: `Valgt ydelse: ${serviceTitle}\n\nBeskrivelse:\n${formData.description}`,
          website_url: formData.website_url,
          source: `Tilbudsside Formular (Valgt: ${serviceTitle})`,
        }),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          description: "",
          website_url: "",
        });
        // Reset to first service if available
        if (services.length > 0) {
          setSelectedService(services[0].id);
        } else {
          setSelectedService("andet");
        }
      } else {
        setSubmitStatus("error");
      }
    } catch (err) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-neutral-50 text-neutral-900 min-h-screen py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-yellow-600 font-bold text-sm uppercase tracking-wider block mb-2">
            Uforpligtende tilbud
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-neutral-900">
            Lad os give et stærkt tilbud på dit projekt
          </h1>
          <p className="mt-4 text-lg text-neutral-600">
            Vælg den ønskede ydelse nedenfor og udfyld dine kontaktoplysninger. Vi vender tilbage med et skræddersyet tilbud inden for 24 timer.
          </p>
        </div>

        {/* USPs Section - Full Width */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-neutral-900 inline-flex items-center gap-3 justify-center">
              <span className="w-2 h-6 bg-yellow-500 rounded-full" />
              Derfor skal du vælge JG Mesterbyg
            </h2>
            <p className="text-neutral-500 text-sm mt-2 max-w-xl mx-auto">
              Hos{" "}
              <Link
                to="/"
                className="text-yellow-600 hover:text-yellow-700 font-bold underline underline-offset-2"
                title="Gå til forsiden"
              >
                JG Mesterbyg
              </Link>{" "}
              leverer vi solidt dansk håndværk med fokus på kvalitet, aftaler og tæt dialog. Se også vores udførte projekter i{" "}
              <Link to="/galleri" className="text-yellow-600 hover:text-yellow-700 font-semibold hover:underline">
                galleriet
              </Link>{" "}
              eller{" "}
              <Link to="/kontakt" className="text-yellow-600 hover:text-yellow-700 font-semibold hover:underline">
                kontakt os
              </Link>
              , hvis du har spørgsmål.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {US_POINTS.map((usp, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl border border-neutral-200 shadow-sm flex flex-col gap-4 hover:shadow-md transition-shadow duration-200">
                <div className="w-12 h-12 rounded-xl bg-yellow-500/10 text-yellow-600 flex items-center justify-center text-2xl shrink-0">
                  <i className={`fa-solid ${usp.icon}`} />
                </div>
                <div>
                  <h3 className="font-bold text-neutral-900 text-base mb-2">{usp.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">{usp.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Merged Webform Section - Centered & Premium Width */}
        <div className="max-w-5xl mx-auto relative">
          {/* Background Glow */}
          <div className="absolute inset-0 bg-yellow-500/5 blur-3xl rounded-full -z-10" />

          <form onSubmit={handleSubmit} className="bg-white p-6 md:p-10 rounded-3xl shadow-xl border border-neutral-200 relative overflow-hidden space-y-8">
            {/* Huge Watermark Logo */}
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/1c3d2574-7326-4533-ae86-8c8f28cf4f21/ikon-jg-mesterbyg-removebg-preview.png" 
              alt="" 
              className="absolute -bottom-24 -right-24 w-[450px] h-[450px] opacity-[0.03] pointer-events-none object-contain"
              aria-hidden="true"
            />

            {/* Form Header */}
            <div className="border-b border-neutral-100 pb-6">
              <h3 className="text-2xl font-bold text-neutral-900 mb-2 flex items-center gap-3">
                <i className="fa-solid fa-file-signature text-yellow-500" />
                Få et uforpligtende tilbud
              </h3>
              <p className="text-sm text-neutral-600">
                Udfyld formularen nedenfor, så vender vi tilbage inden for 24 timer med et uforpligtende tilbud.
              </p>
            </div>

            {/* Status Messages */}
            {submitStatus === "success" && (
              <div className="p-5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl flex items-start gap-4">
                <i className="fa-solid fa-circle-check text-2xl text-emerald-500 mt-0.5" />
                <div>
                  <h4 className="font-bold text-base">Tak for din henvendelse!</h4>
                  <p className="text-sm mt-1">Vi har modtaget din forespørgsel og vender tilbage til dig hurtigst muligt med et tilbud.</p>
                </div>
              </div>
            )}

            {submitStatus === "error" && (
              <div className="p-5 bg-rose-50 border border-rose-200 text-rose-800 rounded-2xl flex items-start gap-4">
                <i className="fa-solid fa-circle-exclamation text-2xl text-rose-500 mt-0.5" />
                <div>
                  <h4 className="font-bold text-base">Der opstod en fejl</h4>
                  <p className="text-sm mt-1">Kunne ikke indsende formularen. Prøv venligst igen eller ring til os direkte.</p>
                </div>
              </div>
            )}

            {/* Step 1: Service Selection */}
            <div className="space-y-4">
              <h4 className="text-lg font-bold text-neutral-900 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-yellow-500 text-neutral-950 flex items-center justify-center text-xs font-bold">1</span>
                Vælg den ydelse du ønsker tilbud på
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allServiceOptions.map((service) => {
                  const isSelected = selectedService === service.id;
                  return (
                    <button
                      key={service.id}
                      type="button"
                      onClick={() => {
                        setSelectedService(service.id);
                        if (errors.service) {
                          setErrors((prev) => ({ ...prev, service: "" }));
                        }
                      }}
                      className={`text-left p-5 rounded-2xl border-2 transition-all duration-200 relative flex flex-col justify-between h-full cursor-pointer focus:outline-none ${
                        isSelected
                          ? "border-yellow-500 bg-yellow-50/20 ring-4 ring-yellow-500/10"
                          : "border-neutral-200 hover:border-neutral-300 bg-white shadow-sm"
                      }`}
                    >
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg transition-colors duration-200 ${
                            isSelected ? "bg-yellow-500 text-neutral-950" : "bg-neutral-100 text-neutral-600"
                          }`}>
                            <i className={`fa-solid ${service.icon || "fa-hammer"}`} />
                          </div>
                          {isSelected && (
                            <span className="w-5 h-5 rounded-full bg-yellow-500 text-neutral-950 flex items-center justify-center text-xs font-bold">
                              <i className="fa-solid fa-check" />
                            </span>
                          )}
                        </div>
                        <h5 className="font-bold text-neutral-900 text-sm mb-1">{service.title}</h5>
                        <p className="text-xs text-neutral-500 leading-relaxed">{service.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
              {errors.service && <p className="text-xs text-rose-500 font-medium">{errors.service}</p>}
            </div>

            {/* Step 2: Contact Details */}
            <div className="space-y-4 pt-4 border-t border-neutral-100">
              <h4 className="text-lg font-bold text-neutral-900 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-yellow-500 text-neutral-950 flex items-center justify-center text-xs font-bold">2</span>
                Dine kontaktoplysninger
              </h4>

              {/* Honeypot field - absolutely hidden from humans */}
              <div className="sr-only absolute -top-9999 -left-9999" aria-hidden="true">
                <label htmlFor="website_url">Do not fill this field</label>
                <input
                  type="text"
                  id="website_url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-neutral-700 mb-1">
                    Navn <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-neutral-400">
                      <i className="fa-solid fa-user text-sm" />
                    </span>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 bg-neutral-50 border ${
                        errors.name ? "border-rose-500 focus:ring-rose-200" : "border-neutral-300 focus:ring-yellow-200"
                      } rounded-xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-4 focus:border-yellow-500 transition-all text-sm`}
                      placeholder="Dit fulde navn"
                    />
                  </div>
                  {errors.name && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-neutral-700 mb-1">
                    Telefon <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-neutral-400">
                      <i className="fa-solid fa-phone text-sm" />
                    </span>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 bg-neutral-50 border ${
                        errors.phone ? "border-rose-500 focus:ring-rose-200" : "border-neutral-300 focus:ring-yellow-200"
                      } rounded-xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-4 focus:border-yellow-500 transition-all text-sm`}
                      placeholder="Telefonnummer"
                    />
                  </div>
                  {errors.phone && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.phone}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-neutral-700 mb-1">
                    E-mail <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-neutral-400">
                      <i className="fa-solid fa-envelope text-sm" />
                    </span>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 bg-neutral-50 border ${
                        errors.email ? "border-rose-500 focus:ring-rose-200" : "border-neutral-300 focus:ring-yellow-200"
                      } rounded-xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-4 focus:border-yellow-500 transition-all text-sm`}
                      placeholder="Din e-mailadresse"
                    />
                  </div>
                  {errors.email && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.email}</p>}
                </div>
              </div>
            </div>

            {/* Step 3: Description */}
            <div className="space-y-4 pt-4 border-t border-neutral-100">
              <h4 className="text-lg font-bold text-neutral-900 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-yellow-500 text-neutral-950 flex items-center justify-center text-xs font-bold">3</span>
                Beskriv dit projekt
              </h4>

              <div>
                <label htmlFor="description" className="block text-xs font-semibold uppercase tracking-wider text-neutral-700 mb-1">
                  Beskrivelse af opgaven <span className="text-rose-500">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="5"
                  className={`w-full px-4 py-3 bg-neutral-50 border ${
                    errors.description ? "border-rose-500 focus:ring-rose-200" : "border-neutral-300 focus:ring-yellow-200"
                  } rounded-xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-4 focus:border-yellow-500 transition-all text-sm resize-none`}
                  placeholder="Beskriv dit projekt (f.eks. omfang, materialer, tidsramme)..."
                />
                {errors.description && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.description}</p>}
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 px-8 bg-yellow-500 hover:bg-yellow-600 text-neutral-950 font-bold rounded-2xl shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-base"
              >
                {isSubmitting ? (
                  <>
                    <i className="fa-solid fa-spinner animate-spin text-lg" />
                    <span>Indsender forespørgsel...</span>
                  </>
                ) : (
                  <>
                    <span>Indsend tilbudsforespørgsel</span>
                    <i className="fa-solid fa-arrow-right text-lg" />
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Direct Contact Info - Placed beautifully under the form */}
          <div className="mt-8 bg-neutral-900 text-white p-6 md:p-8 rounded-3xl shadow-md border border-neutral-800 flex flex-col sm:flex-row justify-between items-center gap-6">
            <div>
              <h4 className="font-bold text-base text-white">Vil du hellere ringe eller skrive direkte?</h4>
              <p className="text-sm text-neutral-400 mt-1">Vi sidder klar til at rådgive dig alle hverdage.</p>
            </div>
            <div className="flex flex-col sm:items-end gap-2 shrink-0">
              <a href={`tel:${CONTACT_INFO.phone}`} className="text-yellow-500 font-bold hover:underline text-base whitespace-nowrap flex items-center gap-2">
                <i className="fa-solid fa-phone" />
                {CONTACT_INFO.phoneDisplay}
              </a>
              <a href={`mailto:${CONTACT_INFO.email}`} className="text-neutral-300 hover:underline text-sm whitespace-nowrap flex items-center gap-2">
                <i className="fa-solid fa-envelope" />
                {CONTACT_INFO.email}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
